import { useRef, forwardRef, useImperativeHandle } from 'react'
import { useFrame } from '@react-three/fiber'
import { Mesh } from 'three'

interface FloatingGeometryProps {
  position: [number, number, number]
  geometry: 'icosahedron' | 'torus' | 'octahedron' | 'cone'
  color?: string
  wireframe?: boolean
}

const FloatingGeometry = forwardRef<Mesh, FloatingGeometryProps>(({ 
  position, 
  geometry, 
  color = '#8b5cf6', 
  wireframe = true 
}, ref) => {
  const meshRef = useRef<Mesh>(null!)

  useImperativeHandle(ref, () => meshRef.current)

  useFrame((state) => {
    if (!meshRef.current) return
    
    const time = state.clock.elapsedTime
    
    // Slower, gentler floating animation
    meshRef.current.position.y = position[1] + Math.sin(time * 0.5 + position[0]) * 0.3
    meshRef.current.rotation.x += 0.005
    meshRef.current.rotation.y += 0.01
    meshRef.current.rotation.z += 0.002
    
    // Minimal scaling to reduce calculations
    const scale = 1 + Math.sin(time + position[0]) * 0.05
    meshRef.current.scale.setScalar(scale)
  })

  const renderGeometry = () => {
    switch (geometry) {
      case 'icosahedron':
        return <icosahedronGeometry args={[1, 1]} />
      case 'torus':
        return <torusGeometry args={[0.8, 0.3, 16, 100]} />
      case 'octahedron':
        return <octahedronGeometry args={[1.2]} />
      case 'cone':
        return <coneGeometry args={[1, 2, 8]} />
      default:
        return <icosahedronGeometry args={[1, 1]} />
    }
  }

  return (
    <mesh ref={meshRef} position={position}>
      {renderGeometry()}
      <meshPhysicalMaterial
        color={color}
        wireframe={wireframe}
        transparent
        opacity={0.6}
        emissive={color}
        emissiveIntensity={0.2}
        roughness={0.1}
        metalness={0.8}
      />
    </mesh>
  )
})

FloatingGeometry.displayName = 'FloatingGeometry'

export default FloatingGeometry
